// Night mode round 2 (2026-10-02) — the dashboard's colour pairs as CLASSES.
//
// The KPI tiles, delta chips and small badges used to take raw hex through
// props ('#DCFCE7' + '#15803D') into inline styles, which night mode cannot
// reach. Each pair is now a tone: the light half is the exact hex it always
// was (a token when one has that very value), the dark half is the status
// token of the same meaning (green done, red stuck, orange waiting) or the
// hue's night tint — so light mode is pixel-identical and dark keeps meaning.

export interface Tone {
  /** a soft background: icon tile, chip */
  tile: string;
  /** the text / figure on it, or on the card */
  ink: string;
}

export const TONE = {
  /** default KPI: grey-50 tile, gray-800 figure */
  neutral: { tile: 'bg-adm-surface-2', ink: 'text-[#1F2937] dark:text-adm-ink' },
  /** quiet: gray-100 chip, gray-500 text (no change, no data) */
  muted: { tile: 'bg-adm-surface-3', ink: 'text-adm-muted' },
  /** gray-100 tile, gray-700 figure */
  slate: { tile: 'bg-adm-surface-3', ink: 'text-adm-ink-2' },
  /** money in: the cream canvas tile, green figure */
  cash: { tile: 'bg-adm-canvas', ink: 'text-status-done' },
  done: { tile: 'bg-[#DCFCE7] dark:bg-status-done-bg', ink: 'text-status-done' },
  stuck: { tile: 'bg-[#FEE2E2] dark:bg-status-stuck-bg', ink: 'text-status-stuck' },
  /** middling (e.g. delivery rate 60–80 %): yellow in light, waiting-orange at night */
  caution: { tile: 'bg-[#FEF9C3] dark:bg-status-waiting-bg', ink: 'text-[#854D0E] dark:text-status-waiting' },
  info: { tile: 'bg-[#EFF6FF] dark:bg-[rgb(59_130_246/0.14)]', ink: 'text-[#1D4ED8] dark:text-[#93C5FD]' },
  violet: { tile: 'bg-[#F3E8FF] dark:bg-[rgb(168_85_247/0.14)]', ink: 'text-[#7E22CE] dark:text-[#D8B4FE]' },
  /** gold pills: 10 % gold wash, deep gold-brown text */
  gold: { tile: 'bg-[rgba(201,162,74,0.10)]', ink: 'text-[#6B5423] dark:text-adm-gold-ink' },
} as const satisfies Record<string, Tone>;

export type ToneName = keyof typeof TONE;

/** tile + ink in one string, for a chip or a pill */
export const chip = (t: ToneName) => `${TONE[t].tile} ${TONE[t].ink}`;

